import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, Check, Mail, Sparkles, Zap, MessageCircle, BarChart3, Target, Share2, Calendar } from 'lucide-react'
import AnimatedBackground from '../components/AnimatedBackground'

const capabilities = [
  { icon: Sparkles, title: 'AI Content Studio', desc: 'Create on-brand posts, captions, hashtags and ad copy with AI.' },
  { icon: Calendar, title: 'Intelligent Scheduling', desc: 'Plan, queue and publish at the right time across channels.' },
  { icon: MessageCircle, title: 'WhatsApp Business', desc: 'Campaigns, broadcasts, automation and conversational engagement.' },
  { icon: BarChart3, title: 'Unified Analytics', desc: 'Measure engagement, attribution, performance and ROI in one place.' },
  { icon: Target, title: 'AI Ad Optimization', desc: 'Optimize targeting, creative, budgets and bids across paid channels.' },
  { icon: Share2, title: 'Omnichannel Publishing', desc: 'Create once, customize by platform and publish everywhere.' },
]

const channels = ['Instagram', 'TikTok', 'LinkedIn', 'Facebook', 'X / Twitter', 'Pinterest', 'YouTube', 'WhatsApp']

const powerFeatures = [
  'AI Chatbot and conversational automation',
  'Content remix and platform-native variations',
  'AI image generation and video optimization',
  'Hashtag discovery and performance tracking',
  'Social listening, sentiment and competitor monitoring',
  'Brand safety and brand-voice controls',
  'Localization and multi-language content',
  'Evergreen content recycling',
  'Team workflows, approvals and role-based access',
  'Centralized brand kit and assets',
  'Influencer relationship and campaign management',
]

export default function ComingSoon() {
  const [copied, setCopied] = useState(false)

  const handleEmail = () => {
    setCopied(true)
    window.location.href = 'mailto:hello@socianist.com?subject=Socianist%20Early%20Access&body=I%27d%20like%20to%20be%20notified%20when%20Socianist%20launches.'
    window.setTimeout(() => setCopied(false), 2500)
  }

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-dark-900 text-white">
      <AnimatedBackground />

      <header className="relative z-20 px-4 sm:px-6 py-5 sm:py-7">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5" aria-label="Socianist home">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-neon-purple to-neon-cyan flex items-center justify-center shadow-lg">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="text-xl sm:text-2xl font-black tracking-tight text-gradient">Socianist</span>
          </a>
          <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-neon-purple/30 bg-neon-purple/10 px-4 py-2 text-xs font-semibold tracking-wide text-neon-purple">
            <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan animate-pulse" />
            COMING SOON
          </span>
        </div>
      </header>

      <main id="top" className="relative z-10">
        <section className="px-4 sm:px-6 pt-14 sm:pt-20 md:pt-28 pb-16 sm:pb-24">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex sm:hidden items-center gap-2 rounded-full border border-neon-purple/30 bg-neon-purple/10 px-4 py-2 text-xs font-semibold tracking-wide text-neon-purple mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan animate-pulse" />
              COMING SOON
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .08 }}>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight">
                The smarter way to
                <br />
                <span className="text-gradient">do social.</span>
              </h1>
            </motion.div>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .18 }} className="mt-6 sm:mt-8 max-w-2xl mx-auto text-base sm:text-lg md:text-xl leading-relaxed text-slate-400">
              Socianist is building an AI-first marketing platform that brings content creation, publishing, WhatsApp, analytics and advertising into one intelligent workspace.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .28 }} className="mt-8 sm:mt-10 flex flex-col sm:flex-row justify-center gap-3">
              <button onClick={handleEmail} className="btn-primary text-sm sm:text-base px-7 sm:px-8">
                {copied ? 'Opening email…' : 'Get Early Access'}
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <a href="mailto:hello@socianist.com" className="btn-secondary text-sm sm:text-base px-7 sm:px-8">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                hello@socianist.com
              </a>
            </motion.div>
            <p className="mt-4 text-xs sm:text-sm text-slate-500">Launching soon. Product features are currently under development.</p>
          </div>
        </section>

        <section className="px-4 sm:px-6 pb-16 sm:pb-24">
          <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((item, i) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * .06 }} className="glass rounded-2xl p-5 sm:p-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neon-purple/80 to-neon-cyan/80 flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-base sm:text-lg font-bold text-white mb-2">{item.title}</h2>
                <p className="text-sm leading-relaxed text-slate-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="px-4 sm:px-6 py-16 sm:py-24 bg-gradient-radial border-y border-white/5">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-neon-cyan mb-4">One platform</div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black">Every channel. One intelligence layer.</h2>
            <p className="mt-4 max-w-2xl mx-auto text-sm sm:text-base text-slate-400">Designed to help teams create once, adapt everywhere, automate repetitive work and understand what actually drives results.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-2.5">
              {channels.map((channel) => <span key={channel} className="glass rounded-full px-4 py-2 text-xs sm:text-sm text-slate-300">{channel}</span>)}
            </div>
          </div>
        </section>

        <section className="px-4 sm:px-6 py-16 sm:py-24">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 md:gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-neon-purple mb-4">Built for the full workflow</div>
              <h2 className="text-3xl sm:text-4xl font-black leading-tight">More than a scheduler.</h2>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-400">The vision goes beyond publishing. Socianist is being built as a complete command center for AI-assisted social marketing, from creation and conversations to measurement and growth.</p>
            </div>
            <div className="glass rounded-2xl p-5 sm:p-7">
              <div className="grid gap-3">
                {powerFeatures.map((feature) => (
                  <div key={feature} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-neon-purple/15 border border-neon-purple/30 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-neon-cyan" /></span>
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 sm:px-6 pb-20 sm:pb-28">
          <div className="max-w-4xl mx-auto">
            <div className="glass glow-purple rounded-3xl p-8 sm:p-12 md:p-16 text-center relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-neon-purple to-transparent" />
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black">Be there when we launch.</h2>
              <p className="mt-4 max-w-xl mx-auto text-sm sm:text-base text-slate-400">Want to hear when early access opens? Send us a quick email and we'll add you to the launch list.</p>
              <button onClick={handleEmail} className="btn-primary mt-7 text-sm sm:text-base">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                {copied ? 'Opening email…' : 'Join the Launch List'}
              </button>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/5 px-4 sm:px-6 py-7">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm text-slate-500">
          <span>© {new Date().getFullYear()} Socianist. All rights reserved.</span>
          <a href="mailto:hello@socianist.com" className="hover:text-white transition-colors">hello@socianist.com</a>
        </div>
      </footer>
    </div>
  )
}
